import { NotificationRecurrenceType } from '@mfw/common';
import qs from 'qs';

import type {
  DeactivateNotificationCenterTemplateArgs,
  FetchNotificationCenterTemplateByIdArgs,
  CreateNotificationCenterTemplateArgs,
  DeleteNotificationCenterTemplateArgs,
  FetchNotificationCenterTemplatesArgs,
  EditNotificationCenterTemplateArgs,
} from './types/notificationCenter.types';
import type {
  ApiNotificationCenter,
  UiNotificationCenter,
} from '../schemas/notificationCenter.schema';
import type {
  BaseApiPaginatedResponse,
  BaseApiResponse,
} from './types/api.types';

import { mapNotificationCenterTemplateFromApi } from './mappers/notificationCenter.mappers';
import { destroy, patch, post, get } from './axios';

const baseUrl = (organizationId: string) =>
  `/v2/organizations/${organizationId}/notification-center`;

function combineDateTime(
  date: Date | null | undefined,
  time: Date | null | undefined,
) {
  if (!date) return;
  const h = time?.getHours() ?? 0;
  const m = time?.getMinutes() ?? 0;
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    h,
    m,
    0,
    0,
  );
}

function mapToApiPayload(data: UiNotificationCenter) {
  const { customDates, endDate, recurrenceType, startDate, time } = data;

  let mappedStartDate: Date | undefined;
  let mappedEndDate: Date | undefined;
  let mappedCustomDates: Date[] | undefined;

  if (recurrenceType === NotificationRecurrenceType.CUSTOM) {
    const sorted = [...(customDates ?? [])].sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime(),
    );
    mappedStartDate = sorted[0];
    mappedEndDate = sorted[sorted.length - 1];
    mappedCustomDates = sorted.length ? sorted : undefined;
  } else if (recurrenceType === NotificationRecurrenceType.EVERY_N_DAYS) {
    mappedStartDate = combineDateTime(startDate, time);
    mappedEndDate = combineDateTime(endDate, time);
  } else if (recurrenceType === NotificationRecurrenceType.FIRST_OF_MONTH) {
    if (startDate) {
      const h = time?.getHours() ?? 0;
      const m = time?.getMinutes() ?? 0;
      mappedStartDate = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        1,
        h,
        m,
        0,
        0,
      );
    }
    if (endDate) {
      const h = time?.getHours() ?? 0;
      const m = time?.getMinutes() ?? 0;
      mappedEndDate = new Date(
        endDate.getFullYear(),
        endDate.getMonth(),
        1,
        h,
        m,
        0,
        0,
      );
    }
  }

  return {
    clientIds: data.clientIds.map((c) => c.value),
    customDates: mappedCustomDates,
    endDate: mappedEndDate,
    // an emptied number field holds null; the DTO takes a number or nothing
    recurrenceInterval: data.recurrenceInterval ?? undefined,
    recurrenceType,
    sendVia: data.sendVia,
    startDate: mappedStartDate,
    translations: data.translations.map((t) => ({
      body: t.description,
      locale: t.locale[0].value,
      title: t.name,
    })),
  };
}

export async function fetchNotificationCenterTemplates({
  filters,
  organizationId,
}: FetchNotificationCenterTemplatesArgs) {
  const queryString = qs.stringify({ ...filters }, { skipNulls: true });
  const response = await get<BaseApiPaginatedResponse<ApiNotificationCenter[]>>(
    `${baseUrl(organizationId)}?${queryString}`,
  );
  if (!response) return null;
  return {
    ...response,
    data: response.data.map(mapNotificationCenterTemplateFromApi),
  };
}

export async function fetchNotificationCenterTemplateById({
  id,
  organizationId,
}: FetchNotificationCenterTemplateByIdArgs) {
  const response = await get<BaseApiResponse<ApiNotificationCenter>>(
    `${baseUrl(organizationId)}/${id}`,
  );
  return response?.data ?? null;
}

export async function createNotificationCenterTemplate({
  data,
  organizationId,
}: CreateNotificationCenterTemplateArgs) {
  const response = await post<ApiNotificationCenter>(
    baseUrl(organizationId),
    mapToApiPayload(data),
  );
  return response?.data ?? null;
}

export async function editNotificationCenterTemplate({
  id,
  data,
  organizationId,
}: EditNotificationCenterTemplateArgs) {
  const response = await patch<ApiNotificationCenter>(
    `${baseUrl(organizationId)}/${id}`,
    mapToApiPayload(data),
  );
  return response?.data ?? null;
}

export async function deleteNotificationCenterTemplate({
  id,
  organizationId,
}: DeleteNotificationCenterTemplateArgs) {
  await destroy(`${baseUrl(organizationId)}/${id}`);
}

export async function deactivateNotificationCenterTemplate({
  id,
  organizationId,
}: DeactivateNotificationCenterTemplateArgs) {
  await patch(`${baseUrl(organizationId)}/${id}/deactivate`, {});
}
